import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, PillButton, Input, Chip, IconCircle, EmptyState, FeatureBanner, Toggle, Modal } from '../ui';
import { chipLabel, cardStatus, canManage, uidSwitchState, uidLoginBody, uidLoginErrorKey } from '../utils/secureCards';
import { apiErrorMessage } from '../utils/apiError';
import { ReactComponent as CreditCardIcon } from '../assets/icons/credit-card.svg';
import { ReactComponent as SmartphoneIcon } from '../assets/icons/smartphone.svg';
import { ReactComponent as EditIcon } from '../assets/icons/edit.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import '../pages/Account.css';

function relativeTime(dateStr, lang, neverLabel) {
  if (!dateStr) return neverLabel;
  const diffSec = Math.round((Date.now() - new Date(dateStr).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  const units = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
  for (const [unit, secs] of units) {
    if (Math.abs(diffSec) >= secs) return rtf.format(-Math.round(diffSec / secs), unit);
  }
  return rtf.format(-diffSec, 'second');
}

/**
 * Cuenta -> Seguridad -> Tarjetas SecureGate (design/SECURE-CARD.md, fase C).
 * GESTIONA tarjetas ya formateadas: nombre, estado, ultimo uso, revocar y el interruptor del login por UID.
 * NO formatea: formatear necesita acceso en bruto al chip (paginas NTAG / ISO-DEP) y Web NFC solo lee y escribe
 * NDEF, asi que solo se puede desde la app de Android (el aviso lo dice).
 *
 * Props: hasPin / hasMasterPassword (de GET /account/security) para pedir el factor correcto al volver a
 * encender el login por UID.
 */
function SecureCardsManager({ hasPin = true, hasMasterPassword = false }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null); // { available, uidLoginDisabled, formatCardSuggested, cards }
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState('');

  const [renaming, setRenaming] = useState(null); // tarjeta que se renombra
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [busy, setBusy] = useState(false);

  const [factorOpen, setFactorOpen] = useState(false);
  const [factorMethod, setFactorMethod] = useState(hasPin ? 'pin' : 'master_password');
  const [factorPin, setFactorPin] = useState('');
  const [factorMaster, setFactorMaster] = useState('');
  const [factorError, setFactorError] = useState('');

  const load = useCallback(() => {
    api.get('/account/security/cards')
      .then((res) => { setData(res.data); setLoadFailed(false); })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openRename = (card) => {
    setRenaming(card);
    setRenameValue(card.name || '');
    setRenameError('');
  };

  const submitRename = async (e) => {
    e.preventDefault();
    const name = renameValue.trim();
    if (!name) { setRenameError(t('apiErrors.codes.invalid_name')); return; }
    setBusy(true); setRenameError('');
    try {
      await api.patch(`/account/security/cards/${renaming.id}`, { name });
      setRenaming(null);
      load();
    } catch (err) {
      setRenameError(apiErrorMessage(err, t, 'account.security.cards.renameError'));
    } finally { setBusy(false); }
  };

  const revoke = async (card) => {
    const label = card.name || t('account.security.cards.defaultName');
    if (!window.confirm(t('account.security.cards.revokeConfirm', { label }))) return;
    setError('');
    try {
      await api.delete(`/account/security/cards/${card.id}`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, t, 'account.security.cards.revokeError'));
    }
  };

  const sw = data ? uidSwitchState(data) : null;

  const setUidLogin = async (enabled, factor) => {
    setBusy(true);
    try {
      await api.put('/account/security/uid-login', uidLoginBody(enabled, factor));
      return true;
    } catch (err) {
      const known = uidLoginErrorKey(err.response?.data);
      const msg = known ? t(known.key, known.vars) : apiErrorMessage(err, t, 'account.security.cards.uidError');
      if (enabled) setFactorError(msg); else setError(msg);
      return false;
    } finally { setBusy(false); }
  };

  const onToggleUid = async (wantEnabled) => {
    setError('');
    if (wantEnabled) {
      // Encenderlo baja la seguridad: el servidor pide PIN o contrasena maestra otra vez.
      setFactorPin(''); setFactorMaster(''); setFactorError('');
      setFactorMethod(hasPin ? 'pin' : 'master_password');
      setFactorOpen(true);
      return;
    }
    if (await setUidLogin(false)) load();
  };

  const submitFactor = async (e) => {
    e.preventDefault();
    setFactorError('');
    const method = factorMethod;
    if (method === 'pin' ? !factorPin : !factorMaster) { setFactorError(t('loginV2.secondFactorRequired')); return; }
    const ok = await setUidLogin(true, { method, pin: factorPin, masterPassword: factorMaster });
    if (ok) {
      setFactorOpen(false);
      setFactorPin(''); setFactorMaster('');
      load();
    }
  };

  if (loading) return null;

  return (
    <>
      {/* Aviso: cuenta que sigue entrando por el numero (UID) de la tarjeta. */}
      {data?.formatCardSuggested && (
        <FeatureBanner
          icon={SmartphoneIcon}
          tone="warn"
          title={t('account.security.cards.formatBannerTitle')}
          subtitle={t('account.security.cards.formatBannerBody')}
        />
      )}

      <Card>
        <h2 className="sgc-account__section-title">{t('account.security.cards.title')}</h2>
        <p className="sgc-account__list-meta" style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>
          {t('account.security.cards.manageOnly')}
        </p>

        {loadFailed && <p className="sgc-login__error">{t('account.security.cards.loadError')}</p>}
        {data && data.available === false && (
          <p className="sgc-account__list-meta">{t('account.security.cards.unavailable')}</p>
        )}
        {error && <p className="sgc-login__error">{error}</p>}

        {data && !data.cards?.length ? (
          <EmptyState
            icon={CreditCardIcon}
            title={t('account.security.cards.empty')}
            description={t('account.security.cards.emptyHint')}
          />
        ) : (
          <div className="sgc-account__list">
            {(data?.cards || []).map((c) => {
              const status = cardStatus(c);
              return (
                <div key={c.id} className="sgc-account__list-item">
                  <IconCircle
                    icon={status === 'revokedCopy' ? AlertTriangleIcon : CreditCardIcon}
                    tone={status === 'active' ? 'success' : status === 'revokedCopy' ? 'danger' : 'surface'}
                    size={36}
                  />
                  <div className="sgc-account__list-info">
                    <p className="sgc-account__list-title">
                      {c.name || t('account.security.cards.defaultName')}
                      <Chip label={chipLabel(c.chipType)} />
                      <Chip label={t(`account.security.cards.status.${status}`)} selected={status === 'active'} />
                    </p>
                    <p className="sgc-account__list-meta">
                      {c.lastUsedAt
                        ? t('account.security.lastUsed', { date: relativeTime(c.lastUsedAt, i18n.language, t('account.methods.never')) })
                        : t('account.security.cards.neverUsed')}
                    </p>
                  </div>
                  {canManage(c) && (
                    <>
                      <button type="button" className="sgc-account__list-action" onClick={() => openRename(c)} title={t('account.security.cards.rename')}>
                        <EditIcon width={16} height={16} aria-hidden="true" />
                      </button>
                      <button type="button" className="sgc-account__list-action" onClick={() => revoke(c)} title={t('account.security.cards.revoke')}>
                        <TrashIcon width={16} height={16} aria-hidden="true" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {sw?.visible && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Toggle
              checked={sw.checked}
              disabled={busy || (sw.checked && !sw.canTurnOff)}
              onChange={onToggleUid}
              label={t('account.security.cards.uidTitle')}
            />
            <p className="sgc-account__list-meta">
              {sw.checked
                ? t('account.security.cards.uidOnNote')
                : t('account.security.cards.uidOffNote')}
            </p>
          </div>
        )}
      </Card>

      <Modal open={!!renaming} onClose={() => setRenaming(null)} title={t('account.security.cards.renameTitle')}>
        <form className="sgc-account__form" onSubmit={submitRename}>
          <Input
            icon={CreditCardIcon}
            label={t('account.security.cards.renameLabel')}
            value={renameValue}
            maxLength={64}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
          />
          {renameError && <p className="sgc-account__form-error">{renameError}</p>}
          <PillButton type="submit" variant="primary" disabled={busy}>{t('common.save')}</PillButton>
        </form>
      </Modal>

      <Modal open={factorOpen} onClose={() => setFactorOpen(false)} title={t('account.security.cards.uidConfirmTitle')}>
        <form className="sgc-account__form" onSubmit={submitFactor}>
          <p className="sgc-account__merge-warning">{t('account.security.cards.uidConfirmBody')}</p>
          {factorMethod === 'pin' ? (
            <Input
              icon={LockIcon}
              label={t('loginV2.pinLabel')}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={12}
              value={factorPin}
              onChange={(e) => setFactorPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
          ) : (
            <Input
              icon={KeyIcon}
              label={t('loginV2.masterPasswordLabel')}
              type="password"
              value={factorMaster}
              onChange={(e) => setFactorMaster(e.target.value)}
              autoFocus
            />
          )}
          {factorError && <p className="sgc-account__form-error">{factorError}</p>}
          {hasPin && hasMasterPassword && (
            <div className="sgc-login__links">
              <button type="button" onClick={() => setFactorMethod((m) => (m === 'pin' ? 'master_password' : 'pin'))}>
                {factorMethod === 'pin' ? t('loginV2.useMasterPasswordInstead') : t('loginV2.usePinInstead')}
              </button>
            </div>
          )}
          <PillButton type="submit" variant="primary" disabled={busy}>{t('account.security.cards.uidTurnOn')}</PillButton>
        </form>
      </Modal>
    </>
  );
}

export default SecureCardsManager;
